/** Global NPC roster: definitions shared by every world, placed per world. */

import { repairAppearance, repairName } from '../character/appearance.js';
import {
  DEFAULT_BEHAVIOR,
  DEFAULT_WANDER_RADIUS,
  isBehavior,
  isWanderRadius,
} from './catalog.js';

const STORAGE_KEY = 'worldcanvas.npcs.v1';

export function repairNpc(record) {
  const source = record && typeof record === 'object' ? record : {};
  return {
    id: Number.isInteger(source.id) && source.id > 0 ? source.id : 0,
    appearance: repairAppearance(source.appearance),
    behavior: isBehavior(source.behavior) ? source.behavior : DEFAULT_BEHAVIOR,
    wanderRadius: isWanderRadius(source.wanderRadius) ? source.wanderRadius : DEFAULT_WANDER_RADIUS,
  };
}

/** Case-insensitive name clash, ignoring the NPC being edited. */
export function npcNameTaken(npcs, name, exceptId = 0) {
  const normalized = repairName(name).toLowerCase();
  if (!normalized) return false;
  return npcs.some((npc) => npc?.id !== exceptId
    && repairName(npc?.appearance?.name).toLowerCase() === normalized);
}

export class NpcRoster {
  constructor(storage = globalThis.localStorage) {
    this.storage = storage;
  }

  _read() {
    let parsed = null;
    try {
      parsed = JSON.parse(this.storage?.getItem(STORAGE_KEY) ?? 'null');
    } catch {
      parsed = null;
    }
    const records = Array.isArray(parsed?.npcs) ? parsed.npcs : [];
    const npcs = records.map(repairNpc).filter((npc) => npc.id > 0);
    const highest = npcs.reduce((max, npc) => Math.max(max, npc.id), 0);
    const nextId = Number.isInteger(parsed?.nextId) ? Math.max(parsed.nextId, highest + 1) : highest + 1;
    return { nextId, npcs };
  }

  _write(state) {
    this.storage?.setItem(STORAGE_KEY, JSON.stringify({ version: 1, ...state }));
  }

  list() {
    return this._read().npcs;
  }

  get(id) {
    return this.list().find((npc) => npc.id === id) ?? null;
  }

  add(appearance, behavior, wanderRadius) {
    const state = this._read();
    const npc = repairNpc({ id: state.nextId, appearance, behavior, wanderRadius });
    // Ids are never reused, so a stale placement in an unswept save cannot
    // resurrect itself as a different character.
    this._write({ nextId: state.nextId + 1, npcs: [...state.npcs, npc] });
    return npc;
  }

  update(id, changes) {
    const state = this._read();
    const existing = state.npcs.find((npc) => npc.id === id);
    if (!existing) return null;
    const updated = repairNpc({ ...existing, ...changes, id });
    this._write({
      nextId: state.nextId,
      npcs: state.npcs.map((npc) => (npc.id === id ? updated : npc)),
    });
    return updated;
  }

  remove(id) {
    const state = this._read();
    const npcs = state.npcs.filter((npc) => npc.id !== id);
    if (npcs.length === state.npcs.length) return false;
    this._write({ nextId: state.nextId, npcs });
    return true;
  }
}
